(() => {
  "use strict";
  //=====================================================
  // Prevent multiple initializations
  //=====================================================
  if (window.__SCRIPT_INTERCEPTOR_INITIALIZED__) return;
  window.__SCRIPT_INTERCEPTOR_INITIALIZED__ = true;

  const CONSENT_ESSENTIAL_ATTRIBUTE = "data-zinus-consent-essential";
  // 2026-07: Rebuy special-casing removed. Rebuy scripts are governed by the
  // generic Secuvy RULES pipeline below, so its categorization in the CMP
  // (currently Necessary) is authoritative. Core rebuy.js coverage is being
  // requested as a remote rules-list entry.
  const GTM_MARKETING_RULE = Object.freeze({
    a: "src",
    v: "www.googletagmanager.com",
    f: "gtm.js",
    n: "googletagmanager_js",
  });

  function getRules() {
    return Array.isArray(window.RULES) ? window.RULES : [];
  }

  function readPersistedConsentDecision() {
    try {
      const raw = localStorage.getItem("secuvy");
      if (!raw) return null;

      const parsed = JSON.parse(decodeURIComponent(raw));
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function hasCompletePersistedConsentDecision(config) {
    const parsed = readPersistedConsentDecision();
    if (!parsed || !config || !Array.isArray(config.services)) return false;

    const necessaryPurpose = String(config.translations?.en?.necessaryText || "").toLowerCase();
    const nonRequiredServices = config.services.filter(service => {
      if (!service || typeof service.name !== "string" || service.required === true) return false;
      const purposes = Array.isArray(service.purposes) ? service.purposes : [];
      return !purposes.some(purpose => {
        const normalized = String(purpose).toLowerCase();
        return normalized === "necessary" || (necessaryPurpose && normalized === necessaryPurpose);
      });
    });

    return (
      nonRequiredServices.length > 0 &&
      nonRequiredServices.every(service => typeof parsed[service.name] === "boolean")
    );
  }

  function enforceDenyByDefaultBeforeCmpBootstrap(node) {
    if (!node || node.tagName !== "SCRIPT") return;

    const src = node.getAttribute("src") || "";
    if (!src) return;

    try {
      const url = new URL(src, location.href);
      if (
        url.hostname !== "consent.zinus.secuvy.com" ||
        url.pathname !== "/common/index.js"
      ) {
        return;
      }

      const config = window.klaroConfig;
      if (!config || !Array.isArray(config.services)) return;
      if (hasCompletePersistedConsentDecision(config)) return;

      const essentialServiceNames = new Set();
      const essentialScripts = [...document.getElementsByTagName("script")].filter(script =>
        script.hasAttribute(CONSENT_ESSENTIAL_ATTRIBUTE),
      );

      for (const rule of getRules()) {
        if (!rule || typeof rule.n !== "string") continue;

        if (rule.a === "id") {
          if (essentialScripts.some(script => script.getAttribute("id") === rule.v)) {
            essentialServiceNames.add(rule.n);
          }
          continue;
        }

        if (rule.a !== "src") continue;

        const sources = essentialScripts
          .map(script => script.getAttribute("src"))
          .filter(Boolean);
        if (
          rule.v === "consent.zinus.secuvy.com" &&
          ["index.js", "script.js", "policy.js"].includes(rule.f)
        ) {
          sources.push(
            "https://consent.zinus.secuvy.com/common/index.js",
            "https://consent.zinus.secuvy.com/common/assets/script.js",
          );
        }

        if (
          sources.some(source => {
            try {
              const scriptUrl = new URL(source, location.href);
              const hostMatches =
                scriptUrl.hostname === rule.v || scriptUrl.hostname.endsWith("." + rule.v);
              const file = scriptUrl.pathname.split("/").filter(Boolean).pop();
              return hostMatches && (!rule.f || rule.f === file);
            } catch (err) {
              return false;
            }
          })
        ) {
          essentialServiceNames.add(rule.n);
        }
      }

      config.default = false;
      config.acceptAll = false;
      config.services.forEach(service => {
        const purposes = Array.isArray(service.purposes) ? service.purposes : [];
        const isNecessary =
          service.required === true ||
          purposes.some(purpose => String(purpose).toLowerCase() === "necessary") ||
          essentialServiceNames.has(service.name);
        service.default = isNecessary;
      });
    } catch (err) {
      console.error("[Secuvy] Error enforcing deny-by-default CMP config:", err);
    }
  }

  function isMarkedConsentEssential(elOrSrc) {
    return typeof elOrSrc !== "string" && elOrSrc?.hasAttribute?.(CONSENT_ESSENTIAL_ATTRIBUTE);
  }
  //=====================================================
  // Bazaarvoice: keep display widgets available; analytics remains consent-controlled.
  //=====================================================
  function isBazaarvoiceLoader(elOrSrc) {
    const src =
      typeof elOrSrc === "string"
        ? elOrSrc
        : elOrSrc?.getAttribute?.("src") || elOrSrc?.getAttribute?.("data-src") || "";

    if (!src) return false;

    try {
      const url = new URL(src, location.href);
      if (url.hostname !== "apps.bazaarvoice.com") return false;

      const displayModulePaths = [
        /^\/apps\/api\/api-\d+(?:\.\d+)+\.js$/,
        /^\/apps\/inline_ratings\/inline_ratings-\d+(?:\.\d+)+\.js$/,
        /^\/apps\/rating_summary\/rating_summary-\d+(?:\.\d+)+\.js$/,
      ];
      if (displayModulePaths.some(pattern => pattern.test(url.pathname))) return true;

      const deploymentRoot = "/deployments/zinusmattress/v2/production/en_US/";
      if (!url.pathname.startsWith(deploymentRoot)) return false;

      const file = url.pathname.slice(deploymentRoot.length);
      return (
        file === "bv.js" ||
        file === "api-config.js" ||
        file === "inline_ratings-config.js" ||
        file === "rating_summary-config.js"
      );
    } catch (err) {
      return false;
    }
  }

  //=====================================================
  // Negate.io bot protection: must not be intercepted (injects inline scripts)
  //=====================================================
  function isNegateLoader(elOrSrc) {
    if (elOrSrc?.hasAttribute?.("data-zinus-negate-loader")) return true;

    const src =
      typeof elOrSrc === "string"
        ? elOrSrc
        : elOrSrc?.getAttribute?.("src") || elOrSrc?.getAttribute?.("data-src") || "";

    if (!src) return false;

    try {
      const url = new URL(src, location.href);
      return url.hostname === "sapi.negate.io" && url.pathname.startsWith("/script/");
    } catch (err) {
      return false;
    }
  }

  //=====================================================
  // Exact, UI-only app runtimes that cannot always be marked before injection.
  //=====================================================
  function isKnownEssentialAppRuntime(elOrSrc) {
    const src =
      typeof elOrSrc === "string"
        ? elOrSrc
        : elOrSrc?.getAttribute?.("src") || elOrSrc?.getAttribute?.("data-src") || "";

    if (!src) return false;

    try {
      const url = new URL(src, location.href);

      if (url.hostname === "cdn.shopify.com") {
        return [
          /^\/extensions\/[^/]+\/heartcoding-sales-and-discounts-\d+\/assets\/hc-countdown\.min\.js$/,
          /^\/extensions\/[^/]+\/sdm-extensions-\d+\/assets\/hc-countdown\.min\.js$/,
          /^\/extensions\/[^/]+\/patrol-\d+\/assets\/patrol-modules\.js$/,
          /^\/extensions\/[^/]+\/orbe-geolocation-\d+\/assets\/orbe\.min\.js$/,
          /^\/extensions\/[^/]+\/orbe-\d+(?:-\d+)*\/assets\/orbe\.min\.js$/,
        ].some(pattern => pattern.test(url.pathname));
      }

      if (url.hostname === "consent.zinus.secuvy.com") {
        return (
          url.pathname === "/common/index.js" ||
          url.pathname === "/common/assets/script.js"
        );
      }

      return (
        url.hostname === "www.zinus.com" &&
        url.pathname === "/cdn/shopifycloud/shop-js/modules/v2/loader.init-shop-cart-sync.en.esm.js"
      );
    } catch (err) {
      return false;
    }
  }

  function isConsentExemptScript(elOrSrc) {
    return (
      isMarkedConsentEssential(elOrSrc) ||
      isBazaarvoiceLoader(elOrSrc) ||
      isNegateLoader(elOrSrc) ||
      isKnownEssentialAppRuntime(elOrSrc)
    );
  }

  //=====================================================
  // Match script source (src attribute) against RULES
  //=====================================================
  function matchSrcRule(src, el = null) {
    if (!src) return null;
    if ((el && isConsentExemptScript(el)) || isConsentExemptScript(src)) return null;

    try {
      const url = new URL(src, location.href);
      const host = url.hostname;
      const file = url.pathname.split("/").filter(Boolean).pop();

      if (host === GTM_MARKETING_RULE.v && url.pathname === "/gtm.js") {
        return GTM_MARKETING_RULE;
      }

      for (const r of getRules()) {
        if (r.a !== "src") continue;
        const domainMatches = host === r.v || host.endsWith(`.${r.v}`);
        if (!domainMatches) continue;

        // If no file specified → match all scripts from that domain
        if (!r.f || r.f === file) return r;
      }
    } catch (err) {
      // Ignore URL parsing failures
    }

    return null;
  }

  //=====================================================
  // Match <script> element against RULES
  //=====================================================
  function matchElementRule(el) {
    if (!el || el.tagName !== "SCRIPT") return null;
    if (isConsentExemptScript(el) || el.hasAttribute("data-zinus-bv-loader")) return null;

    const srcRule = matchSrcRule(el.getAttribute("src"), el);
    if (srcRule) return srcRule;

    for (const r of getRules()) {
      if (r.a === "src") continue;
      const attributeValue = el.getAttribute(r.a);
      if (!attributeValue) continue;

      // Match <script id="">
      if (r.a === "id" && attributeValue === r.v) {
        return r;
      }

    }

    return null;
  }

  //=====================================================
  // Utility: Get cookie value by name
  //=====================================================
  function getCookie(name) {
    // const cookies = document.cookie.split(";").map(c => c.trim());
    // for (const cookie of cookies) {
    //   if (cookie.startsWith(`${name}=`)) {
    //     return cookie.substring(name.length + 1);
    //   }
    // }
    // return null;
    try {
      return localStorage.getItem(name);
    } catch (err) {
      console.error("[Secuvy] Error getting cookie value:", err);
      return null;
    }
  }

  //=====================================================
  // Determine if the script is allowed based on cookie/geo policy
  //=====================================================
  function isScriptAllowed(rule) {
    let consentState = {};

    try {
      const cookieValue = getCookie("secuvy");

      if (!cookieValue) return false;

      const decoded = decodeURIComponent(cookieValue);
      consentState = JSON.parse(decoded);
    } catch (err) {
      consentState = {};
    }

    if (!consentState || typeof consentState !== "object" || Array.isArray(consentState)) {
      return false;
    }

    if (typeof consentState[rule.n] === "boolean") {
      return consentState[rule.n];
    }

    const categoryName = String(rule.n || "").toLowerCase();
    for (const [name, value] of Object.entries(consentState)) {
      if (name.toLowerCase() === categoryName && typeof value === "boolean") {
        return value;
      }
    }

    return false;
  }

  //=====================================================
  // Neutralize or allow execution of script
  //=====================================================
  function neutralizeScript(el, rule) {
    if (!el || el.dataset.neutralized === "1") return;

    el.dataset.neutralized = "1";
    el.setAttribute("data-name", rule.n);
    el.setAttribute("data-type", el.type || "text/javascript");

    const isAllowed = isScriptAllowed(rule);
    el.type = isAllowed ? "text/javascript" : "text/plain";
  }

  //=====================================================
  // Block inline script execution via beforescriptexecute
  //=====================================================
  document.addEventListener(
    "beforescriptexecute",
    e => {
      const el = e.target;

      if (!el || !el.id || isConsentExemptScript(el)) return;

      for (const r of getRules()) {
        if (r.a === "id" && el.id === r.v) {
          neutralizeScript(el, r);
          if (el.type === "text/plain") e.preventDefault();
          break;
        }
      }
    },
    true,
  );

  //=====================================================
  // Neutralize existing static scripts before they run
  //=====================================================
  [...document.getElementsByTagName("script")].forEach(script => {
    const rule = matchElementRule(script);
    if (rule) neutralizeScript(script, rule);
  });

  //=====================================================
  // Patch document.createElement to catch dynamic script creation
  //=====================================================
  const originalCreateElement = Document.prototype.createElement;
  Document.prototype.createElement = function (tag, opts) {
    const el = originalCreateElement.call(this, tag, opts);

    if (tag.toLowerCase() === "script") {
      Object.defineProperty(el, "src", {
        configurable: true,
        enumerable: true,
        get: () => el.getAttribute("src"),
        set: value => {
          const rule = matchSrcRule(value, el);
          if (rule) neutralizeScript(el, rule);
          el.setAttribute("src", value);
        },
      });
    }
    return el;
  };

  //=====================================================
  // Neutralize a script before any DOM insertion starts a fetch.
  //=====================================================
  function prepareScriptForInsertion(script) {
    if (!script || script.tagName !== "SCRIPT") return;

    // 2026-07: deny-by-default disabled. The Secuvy remote config's service
    // defaults (implied consent for US/opt-out regions) are authoritative;
    // rewriting klaroConfig here forced every fresh visitor to denied and
    // disabled consent-gated apps (Rebuy) by default. Paired with the
    // trusted categoryStatus change in secuvy.consent.script.js.
    // enforceDenyByDefaultBeforeCmpBootstrap(script);
    const rule = matchElementRule(script);
    if (rule) neutralizeScript(script, rule);
  }

  function prepareNodeForInsertion(node) {
    if (!node || typeof node !== "object") return;
    if (node.tagName === "SCRIPT") {
      prepareScriptForInsertion(node);
      return;
    }

    const scripts =
      node.querySelectorAll?.("script") || node.getElementsByTagName?.("script") || [];
    for (const script of scripts) prepareScriptForInsertion(script);
  }

  const insertionPrototype =
    typeof Node !== "undefined" && Node.prototype ? Node.prototype : Element.prototype;

  const originalAppendChild = insertionPrototype.appendChild;
  insertionPrototype.appendChild = function (node) {
    prepareNodeForInsertion(node);
    try {
      return originalAppendChild.call(this, node);
    } catch (err) {
      if (node && node.tagName === "SCRIPT") {
        console.warn("[Secuvy] Skipped invalid dynamic script injection:", err);
        return node;
      }
      throw err;
    }
  };

  const originalInsertBefore = insertionPrototype.insertBefore;
  if (typeof originalInsertBefore === "function") {
    insertionPrototype.insertBefore = function (node, referenceNode) {
      prepareNodeForInsertion(node);
      try {
        return originalInsertBefore.call(this, node, referenceNode);
      } catch (err) {
        if (node && node.tagName === "SCRIPT") {
          console.warn("[Secuvy] Skipped invalid dynamic script insertion:", err);
          return node;
        }
        throw err;
      }
    };
  }

  const originalReplaceChild = insertionPrototype.replaceChild;
  if (typeof originalReplaceChild === "function") {
    insertionPrototype.replaceChild = function (node, currentNode) {
      prepareNodeForInsertion(node);
      try {
        return originalReplaceChild.call(this, node, currentNode);
      } catch (err) {
        if (node && node.tagName === "SCRIPT") {
          console.warn("[Secuvy] Skipped invalid dynamic script replacement:", err);
          return currentNode;
        }
        throw err;
      }
    };
  }

  function patchVariadicInsertion(prototype, methodName) {
    if (!prototype || typeof prototype[methodName] !== "function") return;
    const original = prototype[methodName];
    prototype[methodName] = function (...nodes) {
      nodes.forEach(prepareNodeForInsertion);
      return original.apply(this, nodes);
    };
  }

  ["append", "prepend", "before", "after", "replaceWith"].forEach(methodName => {
    patchVariadicInsertion(Element.prototype, methodName);
  });

  //=====================================================
  // Observe DOM for dynamically added scripts
  //=====================================================
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue; // only ELEMENT_NODE

        // Direct <script> element
        if (node.tagName === "SCRIPT" && !(node.dataset && node.dataset.neutralized === "1")) {
          const matchedRule = matchElementRule(node);
          if (matchedRule) neutralizeScript(node, matchedRule);
        }

        // Nested scripts inside added elements
        const scriptNodes = node.getElementsByTagName?.("script") || [];
        for (const script of scriptNodes) {
          if (script.dataset?.neutralized === "1") continue;

          const matchedRule = matchElementRule(script);
          if (matchedRule) neutralizeScript(script, matchedRule);
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
